import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  createIndustrialCostPrimaryAllocation,
  deleteIndustrialCostPrimaryAllocation,
  getIndustrialCostEconomicMovements,
  getIndustrialCostMovementClassifications,
  getIndustrialCostPrimaryAllocations,
  getIndustrialCostStages,
} from '../services/industrialCostService';
import IndustrialCostSectionPlaceholder from '../components/IndustrialCostSectionPlaceholder';
import type {
  CreatePrimaryAllocationPayload,
  EconomicMovement,
  MovementClassification,
  PrimaryAllocation,
  Stage,
} from '../types/industrialCost';

import { esNumberFormat } from '../utils/numberFormat';
function getErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const maybeResponse = error as {
      response?: { data?: { detail?: string } };
      message?: string;
    };

    if (maybeResponse.response?.data?.detail) {
      return maybeResponse.response.data.detail;
    }

    if (maybeResponse.message) {
      return maybeResponse.message;
    }
  }

  return 'Se produjo un error inesperado.';
}

function formatAmount(value: number): string {
  return esNumberFormat({
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatPct(value: number): string {
  return esNumberFormat({
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatDate(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('es-ES').format(date);
}

function formatPeriod(year: number, month: number | null): string {
  if (!month) {
    return String(year);
  }

  return `${year}-${String(month).padStart(2, '0')}`;
}

function getCompletionStatus(totalPct: number): {
  label: string;
  tone: 'success' | 'warning' | 'error' | 'default';
} {
  if (Math.abs(totalPct - 100) < 0.0001) {
    return { label: 'Completo', tone: 'success' };
  }

  if (totalPct === 0) {
    return { label: 'Sin reparto', tone: 'default' };
  }

  if (totalPct > 100) {
    return { label: 'Excedido', tone: 'error' };
  }

  return { label: 'Incompleto', tone: 'warning' };
}

function getStatusChipStyles(
  tone: 'success' | 'warning' | 'error' | 'default',
): Record<string, unknown> {
  if (tone === 'success') {
    return {
      bgcolor: 'rgba(34,197,94,0.14)',
      color: '#86efac',
      border: '1px solid rgba(34,197,94,0.28)',
    };
  }

  if (tone === 'warning') {
    return {
      bgcolor: 'rgba(245,158,11,0.14)',
      color: '#fcd34d',
      border: '1px solid rgba(245,158,11,0.28)',
    };
  }

  if (tone === 'error') {
    return {
      bgcolor: 'rgba(239,68,68,0.14)',
      color: '#fca5a5',
      border: '1px solid rgba(239,68,68,0.28)',
    };
  }

  return {
    bgcolor: 'rgba(148,163,184,0.12)',
    color: '#cbd5e1',
    border: '1px solid rgba(148,163,184,0.2)',
  };
}

function getStageAccent(stage: Stage, index: number): {
  borderColor: string;
  softBg: string;
  amountColor: string;
  badgeBg: string;
  badgeColor: string;
} {
  const primaryPalette = [
    {
      borderColor: 'rgba(56,189,248,0.45)',
      softBg: 'linear-gradient(180deg, rgba(56,189,248,0.12) 0%, rgba(15,23,42,0.72) 100%)',
      amountColor: '#7dd3fc',
      badgeBg: 'rgba(56,189,248,0.14)',
      badgeColor: '#bae6fd',
    },
    {
      borderColor: 'rgba(59,130,246,0.45)',
      softBg: 'linear-gradient(180deg, rgba(59,130,246,0.12) 0%, rgba(15,23,42,0.72) 100%)',
      amountColor: '#93c5fd',
      badgeBg: 'rgba(59,130,246,0.14)',
      badgeColor: '#bfdbfe',
    },
    {
      borderColor: 'rgba(99,102,241,0.45)',
      softBg: 'linear-gradient(180deg, rgba(99,102,241,0.12) 0%, rgba(15,23,42,0.72) 100%)',
      amountColor: '#a5b4fc',
      badgeBg: 'rgba(99,102,241,0.14)',
      badgeColor: '#c7d2fe',
    },
    {
      borderColor: 'rgba(14,165,233,0.45)',
      softBg: 'linear-gradient(180deg, rgba(14,165,233,0.12) 0%, rgba(15,23,42,0.72) 100%)',
      amountColor: '#67e8f9',
      badgeBg: 'rgba(14,165,233,0.14)',
      badgeColor: '#cffafe',
    },
  ];

  const auxiliaryPalette = [
    {
      borderColor: 'rgba(168,85,247,0.45)',
      softBg: 'linear-gradient(180deg, rgba(168,85,247,0.12) 0%, rgba(15,23,42,0.72) 100%)',
      amountColor: '#d8b4fe',
      badgeBg: 'rgba(168,85,247,0.14)',
      badgeColor: '#e9d5ff',
    },
    {
      borderColor: 'rgba(236,72,153,0.45)',
      softBg: 'linear-gradient(180deg, rgba(236,72,153,0.12) 0%, rgba(15,23,42,0.72) 100%)',
      amountColor: '#f9a8d4',
      badgeBg: 'rgba(236,72,153,0.14)',
      badgeColor: '#fbcfe8',
    },
    {
      borderColor: 'rgba(217,70,239,0.45)',
      softBg: 'linear-gradient(180deg, rgba(217,70,239,0.12) 0%, rgba(15,23,42,0.72) 100%)',
      amountColor: '#f0abfc',
      badgeBg: 'rgba(217,70,239,0.14)',
      badgeColor: '#f5d0fe',
    },
  ];

  const palette =
    stage.stage_type === 'primary'
      ? primaryPalette[index % primaryPalette.length]
      : auxiliaryPalette[index % auxiliaryPalette.length];

  return palette;
}

type StageDraftRow = {
  stage_id: string;
  stage_name: string;
  stage_type: Stage['stage_type'];
  allocation_pct: string;
};

type MovementStatusFilter = 'all' | 'no-allocation' | 'incomplete' | 'complete' | 'exceeded';

type SummaryCardProps = {
  label: string;
  value: string;
  helper: string;
};

function SummaryCard({ label, value, helper }: SummaryCardProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid rgba(148,163,184,0.18)',
        bgcolor: 'rgba(15,23,42,0.5)',
        px: 2.25,
        py: 2,
      }}
    >
      <Stack spacing={0.65}>
        <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 700 }}>
          {label}
        </Typography>
        <Typography variant="h5" sx={{ color: '#f8fafc', fontWeight: 900 }}>
          {value}
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.5 }}>
          {helper}
        </Typography>
      </Stack>
    </Paper>
  );
}

export default function IndustrialCostPrimaryAllocationsView() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedMovement, setSelectedMovement] = useState<EconomicMovement | null>(null);
  const [editingRows, setEditingRows] = useState<StageDraftRow[]>([]);
  const [tableSearch, setTableSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<MovementStatusFilter>('all');

  const [movementsQuery, classificationsQuery, stagesQuery, allocationsQuery] = useQueries({
    queries: [
      {
        queryKey: ['industrial-cost-economic-movements'],
        queryFn: getIndustrialCostEconomicMovements,
      },
      {
        queryKey: ['industrial-cost-movement-classifications'],
        queryFn: getIndustrialCostMovementClassifications,
      },
      {
        queryKey: ['industrial-cost-stages'],
        queryFn: getIndustrialCostStages,
      },
      {
        queryKey: ['industrial-cost-primary-allocations'],
        queryFn: getIndustrialCostPrimaryAllocations,
      },
    ],
  });

  const movements = movementsQuery.data ?? [];
  const classifications = classificationsQuery.data ?? [];
  const stages = stagesQuery.data ?? [];
  const allocations = allocationsQuery.data ?? [];

  const stagesMap = useMemo(
    () => new Map<string, Stage>(stages.map((item) => [item.id, item])),
    [stages],
  );

  const classificationsMap = useMemo(
    () => new Map<string, MovementClassification>(classifications.map((item) => [item.movement_id, item])),
    [classifications],
  );

  const eligibleMovements = useMemo(() => {
    return movements
      .filter((movement: EconomicMovement) => {
        const classification = classificationsMap.get(movement.id);

        return (
          classification?.classification_status === 'classified' &&
          classification.economic_type === 'cost' &&
          classification.cost_type === 'indirect'
        );
      })
      .sort((a, b) => {
        const dateA = a.movement_date ? new Date(a.movement_date).getTime() : 0;
        const dateB = b.movement_date ? new Date(b.movement_date).getTime() : 0;
        return dateB - dateA;
      });
  }, [movements, classificationsMap]);

  const activeStages = useMemo(
    () =>
      [...stages]
        .filter((stage) => stage.is_active)
        .sort((a, b) => a.display_order - b.display_order),
    [stages],
  );

  const eligibleMovementMap = useMemo(
    () => new Map<string, EconomicMovement>(eligibleMovements.map((item) => [item.id, item])),
    [eligibleMovements],
  );

  const allocationsByMovement = useMemo(() => {
    const map = new Map<string, PrimaryAllocation[]>();

    allocations.forEach((allocation) => {
      if (!eligibleMovementMap.has(allocation.movement_id)) {
        return;
      }

      const current = map.get(allocation.movement_id) ?? [];
      current.push(allocation);
      map.set(allocation.movement_id, current);
    });

    return map;
  }, [allocations, eligibleMovementMap]);

  const allocationTotalsByMovement = useMemo(() => {
    const totals = new Map<string, number>();

    allocationsByMovement.forEach((movementAllocations, movementId) => {
      const total = movementAllocations.reduce(
        (acc, allocation) => acc + allocation.allocation_pct,
        0,
      );
      totals.set(movementId, total);
    });

    return totals;
  }, [allocationsByMovement]);

  const stageAmountTotals = useMemo(() => {
    const totals = new Map<string, number>();

    activeStages.forEach((stage) => {
      totals.set(stage.id, 0);
    });

    allocationsByMovement.forEach((movementAllocations, movementId) => {
      const movement = eligibleMovementMap.get(movementId);
      if (!movement) {
        return;
      }

      movementAllocations.forEach((allocation) => {
        const current = totals.get(allocation.stage_id) ?? 0;
        const assignedAmount = movement.amount * (allocation.allocation_pct / 100);
        totals.set(allocation.stage_id, current + assignedAmount);
      });
    });

    return totals;
  }, [activeStages, allocationsByMovement, eligibleMovementMap]);

  const totalIndirectAmount = useMemo(
    () => eligibleMovements.reduce((acc, movement) => acc + movement.amount, 0),
    [eligibleMovements],
  );

  const summary = useMemo(() => {
    let complete = 0;
    let incomplete = 0;
    let noAllocation = 0;

    eligibleMovements.forEach((movement) => {
      const totalPct = allocationTotalsByMovement.get(movement.id) ?? 0;

      if (Math.abs(totalPct - 100) < 0.0001) {
        complete += 1;
      } else if (totalPct === 0) {
        noAllocation += 1;
      } else {
        incomplete += 1;
      }
    });

    return {
      movementCount: eligibleMovements.length,
      totalIndirectAmount,
      complete,
      incomplete,
      noAllocation,
      primaryStages: activeStages.filter((stage) => stage.stage_type === 'primary').length,
      auxiliaryStages: activeStages.filter((stage) => stage.stage_type === 'auxiliary').length,
    };
  }, [eligibleMovements, allocationTotalsByMovement, totalIndirectAmount, activeStages]);

  const filteredMovements = useMemo(() => {
    const normalizedSearch = tableSearch.trim().toLowerCase();

    return eligibleMovements.filter((movement) => {
      const movementAllocations = allocationsByMovement.get(movement.id) ?? [];
      const totalPct = allocationTotalsByMovement.get(movement.id) ?? 0;
      const status = getCompletionStatus(totalPct);

      const matchesStatus =
        statusFilter === 'all'
          ? true
          : statusFilter === 'no-allocation'
            ? totalPct === 0
            : statusFilter === 'incomplete'
              ? totalPct > 0 && totalPct < 100
              : statusFilter === 'complete'
                ? Math.abs(totalPct - 100) < 0.0001
                : totalPct > 100;

      if (!matchesStatus) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const stagesText = movementAllocations
        .map((allocation) => {
          const stage = stagesMap.get(allocation.stage_id);
          return `${stage?.name || ''} ${stage?.code || ''} ${formatPct(allocation.allocation_pct)}%`;
        })
        .join(' ');

      const haystack = [
        formatDate(movement.movement_date),
        movement.description,
        movement.account_code,
        formatAmount(movement.amount),
        formatPeriod(movement.period_year, movement.period_month),
        formatPct(totalPct),
        status.label,
        stagesText,
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(normalizedSearch);
    });
  }, [
    eligibleMovements,
    tableSearch,
    statusFilter,
    allocationsByMovement,
    allocationTotalsByMovement,
    stagesMap,
  ]);

  const openMovementEditor = (movement: EconomicMovement) => {
    const movementAllocations = allocationsByMovement.get(movement.id) ?? [];

    const totalsByStage = new Map<string, number>();
    movementAllocations.forEach((allocation) => {
      const current = totalsByStage.get(allocation.stage_id) ?? 0;
      totalsByStage.set(allocation.stage_id, current + allocation.allocation_pct);
    });

    setEditingRows(
      activeStages.map((stage) => ({
        stage_id: stage.id,
        stage_name: stage.name,
        stage_type: stage.stage_type,
        allocation_pct:
          totalsByStage.has(stage.id) ? String(totalsByStage.get(stage.id) ?? 0) : '',
      })),
    );
    setSelectedMovement(movement);
  };

  const closeMovementEditor = () => {
    setSelectedMovement(null);
    setEditingRows([]);
  };

  const handleRowPctChange = (stageId: string, value: string) => {
    setEditingRows((prev) =>
      prev.map((row) =>
        row.stage_id === stageId ? { ...row, allocation_pct: value } : row,
      ),
    );
  };

  const editorTotals = useMemo(() => {
    const totalPct = editingRows.reduce((acc, row) => {
      const parsed = Number(row.allocation_pct);
      if (row.allocation_pct.trim() === '' || Number.isNaN(parsed)) {
        return acc;
      }
      return acc + parsed;
    }, 0);

    const hasInvalidValue = editingRows.some((row) => {
      if (row.allocation_pct.trim() === '') {
        return false;
      }

      const parsed = Number(row.allocation_pct);
      return Number.isNaN(parsed) || parsed < 0;
    });

    return {
      totalPct,
      remainingPct: 100 - totalPct,
      hasInvalidValue,
      isExceeded: totalPct > 100 + 0.0001,
      isComplete: Math.abs(totalPct - 100) < 0.0001,
    };
  }, [editingRows]);

  const saveMovementMutation = useMutation({
    mutationFn: async () => {
      if (!selectedMovement) {
        return;
      }

      const existingAllocations = allocationsByMovement.get(selectedMovement.id) ?? [];
      const nonZeroRows = editingRows
        .map((row) => ({
          stage_id: row.stage_id,
          allocation_pct: Number(row.allocation_pct),
        }))
        .filter(
          (row) =>
            !Number.isNaN(row.allocation_pct) &&
            row.allocation_pct > 0,
        );

      for (const allocation of existingAllocations) {
        await deleteIndustrialCostPrimaryAllocation(allocation.id);
      }

      for (const row of nonZeroRows) {
        const payload: CreatePrimaryAllocationPayload = {
          movement_id: selectedMovement.id,
          stage_id: row.stage_id,
          allocation_pct: row.allocation_pct,
          rule_label: null,
          notes: null,
        };

        await createIndustrialCostPrimaryAllocation(payload);
      }
    },
    onSuccess: async () => {
      enqueueSnackbar('Reparto del movimiento actualizado correctamente.', {
        variant: 'success',
      });
      closeMovementEditor();
      await queryClient.invalidateQueries({
        queryKey: ['industrial-cost-primary-allocations'],
      });
    },
    onError: (mutationError) => {
      enqueueSnackbar(getErrorMessage(mutationError), { variant: 'error' });
    },
  });

  const isLoading =
    movementsQuery.isLoading ||
    classificationsQuery.isLoading ||
    stagesQuery.isLoading ||
    allocationsQuery.isLoading;

  const isError =
    movementsQuery.isError ||
    classificationsQuery.isError ||
    stagesQuery.isError ||
    allocationsQuery.isError;

  const error =
    movementsQuery.error ||
    classificationsQuery.error ||
    stagesQuery.error ||
    allocationsQuery.error;

  const editorCanSave =
    !!selectedMovement && !editorTotals.hasInvalidValue && !editorTotals.isExceeded;

  return (
    <>
      <Stack spacing={3}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(15,23,42,0.66)',
            backdropFilter: 'blur(10px)',
            px: { xs: 2.5, md: 3 },
            py: { xs: 2.5, md: 3 },
          }}
        >
          <Stack spacing={1.1}>
            <Typography variant="h5" sx={{ color: '#f8fafc', fontWeight: 900 }}>
              Reparto primario
            </Typography>

            <Typography variant="body1" sx={{ color: '#cbd5e1', lineHeight: 1.7 }}>
              Aquí repartes cada coste indirecto entre las etapas de producción,
              tanto principales como auxiliares.
            </Typography>

            <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.65 }}>
              La unidad de trabajo es cada movimiento indirecto. El objetivo es dejar
              visible qué movimientos están completos, cuáles están incompletos y qué
              importe está absorbiendo cada etapa.
            </Typography>
          </Stack>
        </Paper>

        {isError ? (
          <Alert severity="error">{getErrorMessage(error)}</Alert>
        ) : isLoading ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: '1px solid rgba(148,163,184,0.18)',
              bgcolor: 'rgba(15,23,42,0.66)',
              px: 3,
              py: 3,
            }}
          >
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              Cargando reparto primario...
            </Typography>
          </Paper>
        ) : eligibleMovements.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: '1px solid rgba(148,163,184,0.18)',
              bgcolor: 'rgba(15,23,42,0.66)',
              px: 3,
              py: 3,
            }}
          >
            <IndustrialCostSectionPlaceholder
              title="Sin movimientos indirectos repartibles"
              description="Necesitas movimientos clasificados como coste indirecto para abrir el reparto primario."
            />
          </Paper>
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(3, minmax(0, 1fr))',
                  xl: 'repeat(6, minmax(0, 1fr))',
                },
                gap: 2,
              }}
            >
              <SummaryCard
                label="Costes indirectos"
                value={String(summary.movementCount)}
                helper="Movimientos indirectos disponibles para repartir."
              />
              <SummaryCard
                label="Importe total CI"
                value={formatAmount(summary.totalIndirectAmount)}
                helper="Suma total de movimientos indirectos elegibles."
              />
              <SummaryCard
                label="Reparto completo"
                value={String(summary.complete)}
                helper="Movimientos con el 100% del reparto ya asignado."
              />
              <SummaryCard
                label="Pendientes"
                value={String(summary.incomplete)}
                helper="Movimientos con reparto parcial y todavía incompleto."
              />
              <SummaryCard
                label="Sin reparto"
                value={String(summary.noAllocation)}
                helper="Movimientos indirectos aún no distribuidos."
              />
              <SummaryCard
                label="Etapas activas"
                value={`${summary.primaryStages} + ${summary.auxiliaryStages}`}
                helper="Principales y auxiliares disponibles para imputar."
              />
            </Box>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                border: '1px solid rgba(148,163,184,0.18)',
                bgcolor: 'rgba(15,23,42,0.66)',
                backdropFilter: 'blur(10px)',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(148,163,184,0.14)' }}>
                <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                  Etapas y coste indirecto asignado
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                  Vista superior para entender qué etapas están absorbiendo más coste.
                </Typography>
              </Box>

              <Box
                sx={{
                  p: 3,
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, minmax(0, 1fr))',
                    xl: 'repeat(3, minmax(0, 1fr))',
                  },
                  gap: 2,
                }}
              >
                {activeStages.map((stage, index) => {
                  const accent = getStageAccent(stage, index);
                  const amount = stageAmountTotals.get(stage.id) ?? 0;
                  const totalBase = summary.totalIndirectAmount > 0 ? summary.totalIndirectAmount : 1;
                  const weightPct = (amount / totalBase) * 100;

                  return (
                    <Paper
                      key={stage.id}
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: `1px solid ${accent.borderColor}`,
                        background: accent.softBg,
                        px: 2.25,
                        py: 2.1,
                      }}
                    >
                      <Stack spacing={1.35}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          spacing={1}
                        >
                          <Box>
                            <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                              {stage.code}
                            </Typography>
                            <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                              {stage.name}
                            </Typography>
                          </Box>

                          <Chip
                            size="small"
                            label={stage.stage_type === 'primary' ? 'Principal' : 'Auxiliar'}
                            sx={{
                              bgcolor: accent.badgeBg,
                              color: accent.badgeColor,
                              border: `1px solid ${accent.borderColor}`,
                              fontWeight: 800,
                            }}
                          />
                        </Stack>

                        <Typography
                          variant="h4"
                          sx={{ color: accent.amountColor, fontWeight: 900, letterSpacing: '-0.02em' }}
                        >
                          {formatAmount(amount)}
                        </Typography>

                        <Typography variant="body2" sx={{ color: '#cbd5e1', lineHeight: 1.6 }}>
                          {formatPct(weightPct)}% del coste indirecto actualmente repartido.
                        </Typography>
                      </Stack>
                    </Paper>
                  );
                })}
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                border: '1px solid rgba(148,163,184,0.18)',
                bgcolor: 'rgba(15,23,42,0.66)',
                backdropFilter: 'blur(10px)',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(148,163,184,0.14)' }}>
                <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                  Mesa de reparto por movimiento
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                  Cada fila representa un coste indirecto. Desde aquí revisas el estado
                  y abres la edición del reparto.
                </Typography>
              </Box>

              <Box
                sx={{
                  px: 3,
                  py: 2.25,
                  borderBottom: '1px solid rgba(148,163,184,0.14)',
                  bgcolor: 'rgba(15,23,42,0.28)',
                }}
              >
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Buscar en toda la tabla"
                    placeholder="Fecha, descripción, cuenta, importe, etapa, estado o periodo"
                    value={tableSearch}
                    onChange={(e) => setTableSearch(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ sx: { color: '#94a3b8' } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#f8fafc',
                        bgcolor: 'rgba(2,6,23,0.38)',
                        '& fieldset': {
                          borderColor: 'rgba(148,163,184,0.22)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(148,163,184,0.34)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'rgba(56,189,248,0.52)',
                        },
                      },
                    }}
                  />

                  <TextField
                    select
                    label="Estado"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as MovementStatusFilter)}
                    size="small"
                    sx={{
                      minWidth: { xs: '100%', md: 220 },
                      '& .MuiOutlinedInput-root': {
                        color: '#f8fafc',
                        bgcolor: 'rgba(2,6,23,0.38)',
                        '& fieldset': {
                          borderColor: 'rgba(148,163,184,0.22)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(148,163,184,0.34)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'rgba(56,189,248,0.52)',
                        },
                      },
                    }}
                    InputLabelProps={{ sx: { color: '#94a3b8' } }}
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="no-allocation">Sin reparto</MenuItem>
                    <MenuItem value="incomplete">Incompletos</MenuItem>
                    <MenuItem value="complete">Completos</MenuItem>
                    <MenuItem value="exceeded">Excedidos</MenuItem>
                  </TextField>
                </Stack>
              </Box>

              {filteredMovements.length === 0 ? (
                <Box sx={{ p: 3 }}>
                  <IndustrialCostSectionPlaceholder
                    title="Sin movimientos para el filtro actual"
                    description="Ajusta la búsqueda o el filtro de estado para volver a ver movimientos indirectos."
                  />
                </Box>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Fecha</TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Descripción</TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }} align="right">
                          Importe
                        </TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Etapas implicadas</TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }} align="right">
                          % completitud
                        </TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Estado</TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }}>Periodo</TableCell>
                        <TableCell sx={{ color: '#94a3b8', fontWeight: 800 }} align="right">
                          Acción
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredMovements.map((movement) => {
                        const movementAllocations = allocationsByMovement.get(movement.id) ?? [];
                        const totalPct = allocationTotalsByMovement.get(movement.id) ?? 0;
                        const status = getCompletionStatus(totalPct);

                        const sortedAllocations = movementAllocations
                          .slice()
                          .sort((a, b) => a.stage_id.localeCompare(b.stage_id));

                        const visibleAllocations = sortedAllocations.slice(0, 2);
                        const hiddenAllocationsCount = Math.max(sortedAllocations.length - 2, 0);

                        return (
                          <TableRow key={movement.id} hover>
                            <TableCell sx={{ color: '#cbd5e1', whiteSpace: 'nowrap' }}>
                              {formatDate(movement.movement_date)}
                            </TableCell>

                            <TableCell sx={{ minWidth: 280 }}>
                              <Stack spacing={0.4}>
                                <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                                  {movement.description}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                                  {movement.account_code || 'Sin cuenta'}
                                </Typography>
                              </Stack>
                            </TableCell>

                            <TableCell align="right" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                              {formatAmount(movement.amount)}
                            </TableCell>

                            <TableCell sx={{ minWidth: 260 }}>
                              {sortedAllocations.length === 0 ? (
                                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                                  Sin etapas asignadas todavía
                                </Typography>
                              ) : (
                                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                  {visibleAllocations.map((allocation) => {
                                    const stage = stagesMap.get(allocation.stage_id);

                                    return (
                                      <Chip
                                        key={allocation.id}
                                        label={`${stage?.name || allocation.stage_id} · ${formatPct(
                                          allocation.allocation_pct,
                                        )}%`}
                                        size="small"
                                        sx={{
                                          bgcolor:
                                            stage?.stage_type === 'primary'
                                              ? 'rgba(59,130,246,0.14)'
                                              : 'rgba(168,85,247,0.14)',
                                          color:
                                            stage?.stage_type === 'primary'
                                              ? '#bfdbfe'
                                              : '#e9d5ff',
                                          border:
                                            stage?.stage_type === 'primary'
                                              ? '1px solid rgba(59,130,246,0.24)'
                                              : '1px solid rgba(168,85,247,0.24)',
                                          fontWeight: 700,
                                        }}
                                      />
                                    );
                                  })}

                                  {hiddenAllocationsCount > 0 ? (
                                    <Chip
                                      label={`+${hiddenAllocationsCount} más`}
                                      size="small"
                                      sx={{
                                        bgcolor: 'rgba(148,163,184,0.12)',
                                        color: '#cbd5e1',
                                        border: '1px solid rgba(148,163,184,0.2)',
                                        fontWeight: 700,
                                      }}
                                    />
                                  ) : null}
                                </Stack>
                              )}
                            </TableCell>

                            <TableCell align="right" sx={{ color: '#f8fafc', fontWeight: 900 }}>
                              {formatPct(totalPct)}%
                            </TableCell>

                            <TableCell>
                              <Chip
                                size="small"
                                label={status.label}
                                sx={{
                                  ...getStatusChipStyles(status.tone),
                                  fontWeight: 800,
                                }}
                              />
                            </TableCell>

                            <TableCell sx={{ color: '#cbd5e1', whiteSpace: 'nowrap' }}>
                              {formatPeriod(movement.period_year, movement.period_month)}
                            </TableCell>

                            <TableCell align="right">
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => openMovementEditor(movement)}
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 800,
                                  borderColor: 'rgba(56,189,248,0.35)',
                                  color: '#bae6fd',
                                  '&:hover': {
                                    borderColor: 'rgba(56,189,248,0.5)',
                                    bgcolor: 'rgba(56,189,248,0.08)',
                                  },
                                }}
                              >
                                Repartir
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Paper>
          </>
        )}
      </Stack>

      <Dialog
        open={!!selectedMovement}
        onClose={() => !saveMovementMutation.isPending && closeMovementEditor()}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            background: 'linear-gradient(180deg, rgba(15,23,42,0.98) 0%, rgba(15,23,42,0.94) 100%)',
            color: '#f8fafc',
            boxShadow: '0 24px 80px rgba(2,6,23,0.55)',
          },
        }}
      >
        <DialogTitle sx={{ color: '#f8fafc', fontWeight: 800 }}>
          Editar reparto primario
        </DialogTitle>

        <DialogContent>
          {selectedMovement ? (
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(148,163,184,0.16)',
                  bgcolor: 'rgba(15,23,42,0.72)',
                  px: 2,
                  py: 1.75,
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                      Movimiento indirecto
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.25 }}>
                      {selectedMovement.description}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 0.65 }}>
                      {formatDate(selectedMovement.movement_date)} ·{' '}
                      {formatPeriod(selectedMovement.period_year, selectedMovement.period_month)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                      Importe
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.25 }}>
                      {formatAmount(selectedMovement.amount)}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              <Alert severity={editorTotals.isComplete ? 'success' : editorTotals.isExceeded ? 'error' : 'warning'}>
                {editorTotals.isComplete
                  ? 'El reparto suma 100%. Este movimiento queda completo para cálculo.'
                  : editorTotals.isExceeded
                    ? `El reparto supera el 100% en ${formatPct(Math.abs(editorTotals.remainingPct))} puntos. Debes corregirlo antes de guardar.`
                    : `El reparto todavía no llega al 100%. Faltan ${formatPct(
                        Math.max(editorTotals.remainingPct, 0),
                      )} puntos por asignar.`}
              </Alert>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(148,163,184,0.16)',
                  bgcolor: 'rgba(15,23,42,0.78)',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid rgba(148,163,184,0.14)',
                    bgcolor: 'rgba(30,41,59,0.42)',
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#f8fafc' }}>
                    Reparto por etapa
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 0.25 }}>
                    Introduce el porcentaje que corresponde a cada etapa. Puedes dejar
                    etapas vacías si no participan.
                  </Typography>
                </Box>

                <Box sx={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800, color: '#94a3b8' }}>Etapa</TableCell>
                        <TableCell sx={{ fontWeight: 800, color: '#94a3b8' }}>Tipo</TableCell>
                        <TableCell sx={{ fontWeight: 800, color: '#94a3b8' }} align="right">
                          % asignado
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800, color: '#94a3b8' }} align="right">
                          Importe asignado
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {editingRows.map((row) => {
                        const pctValue =
                          row.allocation_pct.trim() === '' || Number.isNaN(Number(row.allocation_pct))
                            ? 0
                            : Number(row.allocation_pct);

                        const assignedAmount = selectedMovement.amount * (pctValue / 100);

                        return (
                          <TableRow key={row.stage_id} hover>
                            <TableCell>
                              <Stack spacing={0.35}>
                                <Typography sx={{ fontWeight: 700, color: '#f8fafc' }}>
                                  {row.stage_name}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                                  {stagesMap.get(row.stage_id)?.code || row.stage_id}
                                </Typography>
                              </Stack>
                            </TableCell>

                            <TableCell>
                              <Chip
                                size="small"
                                label={row.stage_type === 'primary' ? 'Principal' : 'Auxiliar'}
                                sx={{
                                  bgcolor:
                                    row.stage_type === 'primary'
                                      ? 'rgba(59,130,246,0.12)'
                                      : 'rgba(168,85,247,0.12)',
                                  color:
                                    row.stage_type === 'primary'
                                      ? '#1d4ed8'
                                      : '#7e22ce',
                                  border:
                                    row.stage_type === 'primary'
                                      ? '1px solid rgba(59,130,246,0.2)'
                                      : '1px solid rgba(168,85,247,0.2)',
                                  fontWeight: 800,
                                }}
                              />
                            </TableCell>

                            <TableCell align="right" sx={{ width: 180 }}>
                              <TextField
                                value={row.allocation_pct}
                                onChange={(e) => handleRowPctChange(row.stage_id, e.target.value)}
                                type="number"
                                size="small"
                                fullWidth
                                inputProps={{ min: 0, step: '0.01' }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    color: '#f8fafc',
                                    bgcolor: 'rgba(2,6,23,0.4)',
                                    '& fieldset': {
                                      borderColor: 'rgba(148,163,184,0.24)',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: 'rgba(148,163,184,0.34)',
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: 'rgba(56,189,248,0.52)',
                                    },
                                  },
                                  '& input': {
                                    textAlign: 'right',
                                  },
                                }}
                              />
                            </TableCell>

                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              {formatAmount(assignedAmount)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>

                <Divider />

                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    bgcolor: 'rgba(30,41,59,0.42)',
                  }}
                >
                  <Stack direction="row" spacing={3}>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                        Total asignado
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 900 }}>
                        {formatPct(editorTotals.totalPct)}%
                      </Typography>
                    </Box>

                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 700 }}>
                        Pendiente
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 900,
                          color: editorTotals.remainingPct < 0 ? '#dc2626' : 'inherit',
                        }}
                      >
                        {formatPct(editorTotals.remainingPct)}%
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Paper>

              <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.7 }}>
                Esta edición rehace el reparto del movimiento usando las operaciones
                disponibles en v1 (eliminación de líneas existentes y creación de nuevas
                líneas con los porcentajes actuales).
              </Typography>
            </Stack>
          ) : null}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 2.5,
            bgcolor: 'rgba(15,23,42,0.92)',
          }}
        >
          <Button
            onClick={closeMovementEditor}
            disabled={saveMovementMutation.isPending}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={() => saveMovementMutation.mutate()}
            disabled={!editorCanSave || saveMovementMutation.isPending}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: '#38bdf8',
              color: '#0f172a',
              '&:hover': {
                bgcolor: '#38bdf8',
                filter: 'brightness(1.06)',
              },
            }}
          >
            {saveMovementMutation.isPending ? 'Guardando...' : 'Guardar reparto'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}